import { Offer } from '../../../../new-lib/types/entity';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { getPlanDuration } from '../../../../helpers/payment-plan';
import { useRouter } from 'next/router';
import OfferPlanIcon from '../../../../new-components/svg-icons/payment-plan/OfferPlanIcon';

const CompoundOfferPlan = ({ offer }: { offer: Offer }) => {
  const { t } = useTranslation(['entity', 'common']);
  const { locale } = useRouter();

  const getCardContent = () => {
    const {
      cashDiscountPercentage,
      downPaymentPercentage,
      originalDownPaymentPercentage,
      years,
      originalYears,
      discountPercentage
    } = offer;

    if (cashDiscountPercentage) {
      return (
        <div className="offer-content">
          <div className="percentage-container">
            <span className="percentage">
              {Math.round(cashDiscountPercentage)}%
            </span>
            <span className="cash-discount-text">{t('cash_discount')}</span>
          </div>
        </div>
      );
    }

    if (downPaymentPercentage > 0 && !discountPercentage) {
      return (
        <div className="offer-content">
          <div className="percentage-container">
            <span className="percentage">{downPaymentPercentage}%</span>
            <span className="discount-details">{t('down_payment')}</span>
          </div>
          <div className="plan-duration">
            <span className="plan-years">
              {getPlanDuration({
                years: years
              })}{' '}
            </span>
            <span className="discount-details years">{t('years')}</span>
          </div>
        </div>
      );
    }

    const isStrikedThroughPlan =
      (originalDownPaymentPercentage !== null &&
        downPaymentPercentage !== null &&
        originalDownPaymentPercentage !== downPaymentPercentage) ||
      (originalYears !== null && years !== null && originalYears !== years);

    return (
      <div className="offer-content-container">
        <div className="offer-content">
          <div className="percentage-container">
            <span className="percentage">
              {isStrikedThroughPlan
                ? downPaymentPercentage
                : discountPercentage}
              %
            </span>
            <span className="discount-details">
              {isStrikedThroughPlan ? t('down_payment') : t('discount')}
            </span>
          </div>
          {isStrikedThroughPlan && (
            <div className="plan-duration">
              <span className="plan-years">
                {getPlanDuration({
                  years
                })}{' '}
              </span>
              <span className="discount-details years">{t('years')}</span>
            </div>
          )}
        </div>
        {(downPaymentPercentage || originalDownPaymentPercentage) && (
          <span
            className={`payment-plan-container ${isStrikedThroughPlan ? 'striked' : ''}`}
          >
            {!isStrikedThroughPlan && <>{t('valid_on')} </>}
            {isStrikedThroughPlan
              ? originalDownPaymentPercentage
              : downPaymentPercentage}
            % {t('downpayment')}
            {', '}
            {getPlanDuration({
              years: isStrikedThroughPlan ? originalYears : years
            })}{' '}
            {t('years')}
          </span>
        )}
      </div>
    );
  };
  return (
    <OfferPlanContainer locale={locale}>
      <span className="offer-tag">
        <OfferPlanIcon />
        {t('offer')}
      </span>
      <div className="offer-details">{getCardContent()}</div>
      {offer.isBackLoaded && (
        <div className="plan-type">
          {t('escalating_payments', { ns: 'common' })}
        </div>
      )}
    </OfferPlanContainer>
  );
};

const OfferPlanContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-width: 300px;
  max-width: 360px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  gap: 8px;
  border: 0.5px solid #edeff3;
  position: relative;
  .offer-tag {
    color: #ff9108;
    font-family: materialRegular;
    font-size: 16px;
    line-height: 16px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .plan-type {
    position: absolute;
    top: 12px;
    inset-inline-end: 12px;
    font-family: materialMedium;
    font-size: 16px;
    color: #3e4042;
    display: flex;
    align-items: center;
    border-radius: 100px;
    background: #f2f6fd;
    padding: 4px 8px;
  }

  .offer-details {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${props => props.theme.light.colors.black};
    gap: 12px;
    background-color: #fff;

    .offer-tag {
      position: absolute;
      top: 0px;
      inset-inline-end: 0;

      ${props => props.locale === 'ar' && 'transform: scaleX(-1)'}
    }

    .offer-content-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 4px;
      .payment-plan-container {
        color: #2c3138;
        font-size: 12px;
        font-family: materialRegular;
      }
      .striked {
        text-decoration: line-through;
      }
    }

    .offer-content {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 8px;

      .discount-details {
        color: #2c3138;
        font-family: materialRegular;
        font-size: 14px;
        margin-bottom: 4px;

        &.discount {
          width: 100%;
          color: #4b535f;
          font-family: materialRegular;
          font-size: 8px;
        }

        &.years {
          width: unset;
          font-family: materialSemibold;
          font-size: 16px;
        }
      }

      .plan-duration {
        display: flex;
        color: #2c3138;
        font-family: materialSemibold;
        gap: 3px;
      }

      .percentage-container {
        display: flex;
        align-items: flex-end;
        gap: 4px;

        .percentage {
          color: #2c3138;

          font-family: materialBold;
          font-size: 28px;
        }

        .cash-discount-text {
          color: #2c3138;
          font-family: materialRegular;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .discount-text {
          width: 50px;
          font-family: materialRegular;
          font-size: 12px;
          align-self: flex-end;
          color: #2c3138;
          margin-bottom: 4px;
        }

        .percentage-text {
          font-size: ${props => props.theme.font.sizes.xxs};
          color: ${props => props.theme.light.colors.black};
          width: 100%;
        }
      }

      .offer-content-right {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 0 8px;

        .installment-years {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-family: materialSemiBold;
          color: ${props => props.theme.light.colors.black};

          .years {
            font-size: ${props => props.theme.font.sizes.xl};
          }
        }

        .limited-offer {
          color: #2c3138;
          font-size: ${props => props.theme.font.sizes.s};
          font-family: materialBold;
          text-transform: capitalize;
        }
      }
    }

    .escalating-section {
      min-width: 40%;
      max-width: 40%;
      display: flex;
      gap: 12px;
    }

    .separator {
      width: 0.5px;
      height: 33px;
      background: #d3dbe6;
    }

    .offer-name {
      font-size: ${props => props.theme.font.sizes.xxs};
      color: #666;
    }
  }

  @media screen and (max-width: 768px) {
    .offer-tag {
      font-size: 12px;
    }
    .plan-years {
      font-size: 14px;
    }
    .offer-details {
      .offer-content {
        .discount-details {
          font-size: 12px;
          margin-bottom: 0;

          &.years {
            font-size: 14px;
          }
        }

        .percentage-container {
          .percentage {
            font-size: 22px;
          }

          .cash-discount-text {
            font-size: 12px;
          }
        }
      }
    }
  }
`;

export default CompoundOfferPlan;
